import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "@store/thunks/authAction";
import { setUnreadCount } from "@store/slices/dashboardSlice";
import { BASE_URL, ROLE_ROUTES } from "@utils/constants";
import mesh from '@assets/images/Mesh.png';
import LoginTextInput from "@/components/shared/LoginTextInput";
import JSEncrypt from "jsencrypt";
import Header from "@/components/tables/dashboard/Header";
import { ArrowRight, LogIn } from 'lucide-react';
import { loginStudent } from "@/store/thunks/studentLogin";
import { toast } from "react-toastify";
import '../../../styles/App.css'
import { clearError } from "@/store/slices/studentAuthSlice";


const Modal = ({ open, onClose, children }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="bg-white rounded-2xl p-6 w-[90%] max-w-md shadow-xl relative">

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>

        {children}
      </div>
    </div>
  );
};

const checkPasswordRules = (value = "") => ({
  minLength: value.length >= 8,
  hasUppercase: /[A-Z]/.test(value),
  hasLowercase: /[a-z]/.test(value),
  hasNumber: /[0-9]/.test(value),
  hasSpecialChar: /[@#$%^&+=]/.test(value),
});

const PasswordTooltip = ({ password }) => {
  const rules = checkPasswordRules(password);

  const Rule = ({ ok, text }) => (
    <div className={`text-xs flex items-center gap-2 ${ok ? "text-green-600" : "text-gray-500"}`}>
      <span>{ok ? "✔" : "•"}</span>
      {text}
    </div>
  );

  return (
    <div className="absolute z-50 bg-white border rounded-lg p-3 shadow-lg w-64">
      <p className="text-xs font-semibold mb-2 text-gray-700">
        Password must contain:
      </p>
      <Rule ok={rules.minLength} text="At least 8 characters" />
      <Rule ok={rules.hasUppercase} text="One uppercase letter" />
      <Rule ok={rules.hasLowercase} text="One lowercase letter" />
      <Rule ok={rules.hasNumber} text="One number" />
      <Rule ok={rules.hasSpecialChar} text="One special character (@#$%^&+=)" />
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("active");
  const {
    isLoading,
    error,
    isAuthenticated,
    role,
    userId,
    token
  } = useSelector((state) =>
    activeTab === "active" ? state.studentAuth : state.auth
  );

  const {
    selectedLanguage
  } = useSelector((state) =>
    state.loginPage
  );

  const [publicKey, setPublicKey] = useState(null);
  const [loginCaptcha, setLoginCaptcha] = useState({
    captchaImage: null,
    captchaToken: "",
  });

  const [forgotCaptcha, setForgotCaptcha] = useState({
    captchaImage: null,
    captchaToken: "",
  });
  const [forgotCaptchaLoading, setForgotCaptchaLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotUsernameForm, setShowForgotUsernameForm] = useState(false);
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState("FORM");
  const [forgotPasswordOTP, setForgotPasswordOTP] = useState("");
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [forgotPasswordToken, setForgotPasswordToken] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);


  const [forgotData, setForgotData] = useState({
    identifier: "",
    captchaInput: "",
  });

  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    mobile: "",
    captchaInput: "",
  });

  const [resetPasswordData, setResetPasswordData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");



  const handleForgotChange = (key, value) => {
    setForgotData((prev) => ({ ...prev, [key]: value }));
  };

  const handleForgotPasswordChange = (key, value) => {
    setForgotPasswordData((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetPasswordChange = (key, value) => {
    setResetPasswordData(prev => ({ ...prev, [key]: value }));
  };


  // Fetch CAPTCHA from API
  const fetchLoginCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaError(null);
    try {
      const res = await fetch(`${BASE_URL}api/user/captcha`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (data.captchaImage && data.captchaToken) {
        setLoginCaptcha(data);
      }
    } catch (err) {
      setCaptchaError("Failed to load CAPTCHA");
    } finally {
      setCaptchaLoading(false);
    }
  };

  const fetchForgotCaptcha = async () => {
    setForgotCaptchaLoading(true);
    try {
      const res = await fetch(`${BASE_URL}api/user/captcha`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (data.captchaImage && data.captchaToken) {
        setForgotCaptcha(data);
      }
    } catch (err) {
      console.error("Forgot CAPTCHA error", err);
    } finally {
      setForgotCaptchaLoading(false);
    }
  };

  useEffect(() => {
  dispatch(clearError());
}, [dispatch]);

  useEffect(() => {
    fetchLoginCaptcha();
    fetchKey();
  }, []);

  useEffect(() => {
    if (showForgotUsernameForm || showForgotPasswordForm) {
      fetchForgotCaptcha();
    }
  }, [showForgotUsernameForm, showForgotPasswordForm]);



  const redirectToregister = () => {
    navigate('/register');
  };


  // Fetch public key from API
  const fetchKey = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/user/public-key`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      console.log("Public Key fetched:", data);
      if (data.data) {
        setPublicKey(data.data);
      } else {
        console.error("Failed to fetch public key");
      }
    } catch (err) {
      console.error("[fetchKey] Error:", err);
    }
  };

  // Fetch CAPTCHA on mount
  useEffect(() => {
    fetchLoginCaptcha();
    fetchKey();
  }, []);


  // Refresh CAPTCHA when error changes
  useEffect(() => {
    if (error && error !== 'No session found') {
      fetchLoginCaptcha();
      fetchKey();
    }
  }, [error]);



  useEffect(() => {
    let eventSource;

    if (isAuthenticated && role) {
      const url = activeTab === 'active' ? `${BASE_URL}api/notification/subscribe?userId=${userId}` : `${BASE_URL}api/notification/subscribe?userId=${userId}`;
      eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
      };

      const handleOpenNotifications = async () => {
        try {
          const res = await fetch
            (`${BASE_URL}api/notification/fetch?userId=${userId}`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              credentials: "include",
            });

          const data = await res.json();
          dispatch(setUnreadCount(data.data.length > 0 ? data.data.filter((n) => !n.isRead).length : 0));
        } catch (err) {
          console.error("Error fetching notifications:", err);
        }
      };

      handleOpenNotifications();

      eventSource.onerror = (err) => {
        console.error("SSE error:", err);
        eventSource.close();
      };

      const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;
      navigate(destinationRoute);
    }

    return () => {
      if (eventSource) eventSource.close();
    };
  }, [isAuthenticated, role, userId, navigate, dispatch]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (forgotPasswordData.email) {
        checkEmailExists(forgotPasswordData.email);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [forgotPasswordData.email]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (forgotPasswordData.mobile) {
        checkMobileExists(forgotPasswordData.mobile);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [forgotPasswordData.mobile]);

  const onClickAbout = () => {
    navigate("/about-sangraha")
  }

  const sendOTP = async () => {
    const { identifier, captchaInput } = forgotData;
    const captchaToken = forgotCaptcha.captchaToken;

    if (!identifier || !captchaInput) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}api/user/forgot-username/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          identifier,        // email or mobile
          captchaText: captchaInput,
          captchaToken,
        }),
      });

      const data = await res.json();

      if (data.success || data.status === "success") {
        if (data.message === 'User is not registered') {
          toast.error(data.message);
          return;
        }
        else {
          toast.success("A SMS with your username has been sent to this mobile number if registered.");
        }
      } else {
        toast.error(data.message || "Failed to send OTP");
        fetchForgotCaptcha(); // refresh captcha on failure
      }
    } catch (err) {
      console.error("OTP error:", err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setShowForgotUsernameForm(false);
    }
  };

  const sendForgotPasswordOTP = async () => {
    const { email, mobile, captchaInput } = forgotPasswordData;
    const captchaToken = forgotCaptcha.captchaToken;

    if (!email || !mobile || !captchaInput) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}api/user/forgot-password/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          mobile,
          captchaText: captchaInput,
          captchaToken,
        }),
      });

      const data = await res.json();

      if (data.success || data.status === "success") {
        toast.success(data.message);
        setForgotPasswordStep("OTP"); // 🔥 switch UI
      } else {
        toast.error(data.message || "Failed to send OTP");
        fetchForgotCaptcha();
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const verifyForgotPasswordOTP = async () => {
    if (forgotPasswordOTP.length !== 6) {
      toast.error("Enter 6 digit OTP");
      return;
    }

    setVerifyingOTP(true);

    try {
      const res = await fetch(`${BASE_URL}api/user/forgot-password/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          otp: forgotPasswordOTP,
          identifier: forgotPasswordData.email,
        }),
      });

      const data = await res.json();

      if (data.success) {
        toast.success("OTP verified successfully!");
        setForgotPasswordToken(data.data); // store token
        setForgotPasswordStep("RESET");
      } else {
        toast.error(data.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error("OTP verification failed");
    } finally {
      setVerifyingOTP(false);
    }
  };


  const resetPassword = async () => {
    const { password, confirmPassword } = resetPasswordData;

    if (!password || !confirmPassword) {
      toast.error("Please fill all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKey);

    try {
      const res = await fetch(
        `${BASE_URL}api/user/forgot-password/reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${forgotPasswordToken}` },
          credentials: "include",
          body: JSON.stringify({
            newPassword: encrypt.encrypt(password),
          }),
        }
      );

      const data = await res.json();

      if (data.success) {
        toast.success("Password reset successfully!");
        setShowForgotPasswordForm(false);
        setForgotPasswordStep("FORM");
      } else {
        toast.error(data.message || "Password reset failed");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setForgotPasswordToken("");
    }
  };

  const checkEmailExists = async (email) => {
    if (!email) {
      setEmailError("");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}api/user/register/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      setEmailError(data.message || "");
    } catch (error) {
      console.error("Email check failed:", error);
      setEmailError("Error checking email");
    }
  };

  const checkMobileExists = async (mobile) => {
    if (!mobile) {
      setMobileError("");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}api/user/register/mobile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });

      const data = await res.json();
      setMobileError(data.message || "");
    } catch (error) {
      console.error("Mobile check failed:", error);
      setMobileError("Error checking mobile");
    }
  };

  const content = [
    {
      name: {
        hindi: "भारती संग्रह",
        english: "Bharati Sangraha"
      }
    },
    {
      description: {
        hindi: [
          "'भारती संग्रह' भारत की भाषाई विविधता को डिजिटल प्रौद्योगिकी में समाहित करने की दिशा में एक महत्त्वपूर्ण पहल है। यह 'भारती बहुभाषी अनुवाद सारथी' सॉफ्टवेयर के अंतर्गत भारतीय भाषाओं के लिए उच्च गुणवत्ता वाले बहुभाषी डेटा के निर्माण, संग्रहण और प्रबंधन का कार्य करता है।",
          "इस पहल का उद्देश्य भारतीय भाषाओं में कृत्रिम बुद्धिमत्ता एवं मशीन अनुवाद तकनीकों को सक्षम बनाना है, ताकि नागरिकों तक बेहतर डिजिटल सेवाएं उनकी अपनी भाषाओं में पहुंचाई जा सकें और भारत की डिजिटल अवसंरचना को सशक्त बनाया जा सके।"
        ],
        english: [
          "Bharati Sangraha is a significant initiative aimed at integrating India’s linguistic diversity into digital technology. Under the Bharati Bahubhashi Anuvada Sarthi initiative, it focuses on creating, collecting, and managing high-quality multilingual datasets for Indian languages.",
          "The initiative enables AI and machine translation technologies in Indian languages, ensuring better digital services for citizens in their preferred languages while strengthening India's digital infrastructure."
        ]
      }
    },
    {
      subheading: {
        hindi: "मुख्य विशेषताएँ",
        english: "Key Highlights"
      }
    },
    {
      points: [
        {
          hindi: "सरकारी एवं सार्वजनिक संस्थाओं, विश्वविद्यालयों, शोधकर्ताओं और भाषा विशेषज्ञों को एकीकृत डिजिटल मंच पर सहयोग का अवसर प्रदान करता है।",
          english: "Brings together government bodies, public institutions, universities, researchers, and language experts on a unified digital platform."
        },
        {
          hindi: "भारतीय भाषाओं की विविधता को सुरक्षित रखते हुए कृत्रिम बुद्धिमत्ता और उभरती तकनीकों में उनके प्रभावी उपयोग को सक्षम बनाता है।",
          english: "Enables effective use of Indian languages in AI and emerging technologies while preserving linguistic diversity."
        },
        {
          hindi: "उच्च गुणवत्ता वाले भाषा डेटा के माध्यम से भाषा मॉडल्स की सटीकता और गुणवत्ता में सुधार करता है।",
          english: "Improves the quality and accuracy of language models through high-quality linguistic datasets."
        }
      ]
    },
    {
      link: {
        hindi: "भारती संग्रह के बारे में और जानें",
        english: "Learn More About Bharati Sangraha"
      }
    }
  ];

  const isHindi = selectedLanguage === "hindi";

  const fontFamily = isHindi
    ? 'font-hindi'
    : 'font-english';


  const headingSize = isHindi ? "text-[45px]" : "text-[40px]";
  const paragraphSize = isHindi ? "text-[17px]" : "text-[16px]";
  const subheadingSize = isHindi ? "text-[22px]" : "text-[22px]";



  return (
    <>
      <Header />
      <div
        className="min-h-[calc(100vh-97px)] flex flex-col items-center justify-center gap-6 px-2"
        style={{
          backgroundImage: `url(${mesh})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >


        <div className="flex gap-4 self-start w-full max-w-6xl mx-auto px-2">
          {/* <button
            onClick={() => setActiveTab("active")}
            className={`bg-white px-4 py-2 text-[rgba(236,112,38,1)] rounded-xl font-medium transition ${activeTab === "active"
              ? "border border-[rgba(236,112,38,1)] opacity-100"
              : "opacity-50"
              }`}
          >
            Student Login
          </button>

          <button
            onClick={() => setActiveTab("rejected")}
            className={`bg-white px-4 py-2 rounded-xl font-medium text-[rgba(236,112,38,1)] transition ${activeTab === "rejected"
              ? "border border-[rgba(236,112,38,1)] opacity-100"
              : "opacity-50"
              }`}
          >
            GA / Devnagri Login
          </button> */}
        </div>
        <div className="max-w-6xl flex items-center gap-2 bg-white rounded-3xl shadow-2xl overflow-hidden p-10 justify-around">
          {/* Left Section: Login Form */}
          <div className="lg:w-2/5 w-2/5 p-2 lg:p-10 flex flex-col justify-center shadow-lg gradient-top-border rounded-xl">
            <div className="max-w-md mx-auto w-full">
              <h1 className="text-[rgba(236,112,38,1)] font-bold text-2xl text-center uppercase mb-5">
                L
                <span className="underline underline-offset-8">OGI</span>
                N
              </h1>


              {(error !== 'No session found' || error !== 'No session found') && error && (
                <div className="flex items-center text-red-600 text-sm font-['Semantic/Strong'] mb-6 bg-red-50 p-4 rounded-lg border border-red-200">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )}

              <Formik
                key={activeTab}
                initialValues={{ username: "", password: "", captchaInput: "" }}
                validationSchema={Yup.object({
                  username: Yup.string().required("Username is required"),
                  password: Yup.string().required("Password is required"),
                  captchaInput: Yup.string().required("CAPTCHA is required"),
                })}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    let encryptedPassword;
                    if (publicKey) {
                      const encrypt = new JSEncrypt();
                      encrypt.setPublicKey(publicKey);
                      encryptedPassword = encrypt.encrypt(values.password.trim(""));
                    } else {
                      console.error("Public key not available. Cannot encrypt password.");
                      throw new Error("Encryption error. Please try again later.");
                    }
                    if (activeTab === 'active') {
                      await dispatch(loginStudent({
                        username: values.username.trim(""),
                        password: encryptedPassword,
                        captchaInput: values.captchaInput,
                        captchaToken: loginCaptcha.captchaToken,
                      }));
                    } else {
                      await dispatch(loginUser({
                        username: values.username,
                        password: encryptedPassword,
                        captchaInput: values.captchaInput,
                        captchaToken: loginCaptcha.captchaToken,
                      }));
                    }
                  } catch (error) {
                    setCaptchaError(error.message === "Invalid CAPTCHA" ? "Invalid CAPTCHA. Please try again." : null);
                    console.error("Login failed:", error);
                    fetchCaptcha();
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-5 flex flex-col">
                    <LoginTextInput
                      label="Username"
                      labelClassName="text-[15px] font-bold"
                      name="username"
                      type="text"
                      autoComplete="off"
                      placeholder="Enter your username"
                      icon={
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      }
                    />

                    <div className="flex flex-col">
                      <LoginTextInput
                        label="Password"
                        name="password"
                        labelClassName="text-[15px] font-bold"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Enter your password"
                        icon={
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        }
                        endAdornment={
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="text-[#ec7026] hover:text-[#d85d1a] focus:outline-none transition-colors duration-200"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        }
                      />
                      {activeTab === 'active' && <div className="flex flex-row justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            setForgotData({ identifier: "", captchaInput: "" });
                            setShowForgotPasswordForm(false);
                            setShowForgotUsernameForm(true);
                          }}

                          className="text-right text-[15px] text-[rgba(236,112,38,1)] mt-2 hover:underline"
                        >
                          Forgot Username
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setForgotPasswordData({ email: "", mobile: "", captchaInput: "" });

                            setEmailError("");
                            setMobileError("");
                            setShowForgotUsernameForm(false);
                            setShowForgotPasswordForm(true);
                          }}

                          className="text-right text-[15px] text-[rgba(236,112,38,1)] mt-2 hover:underline"
                        >
                          Forgot Password
                        </button>
                      </div>
                      }
                    </div>

                    <div className="flex flex-col">
                      {captchaLoading ? (
                        <div className="flex flex-col items-center justify-center w-40 bg-gray-100 rounded-lg border border-gray-200">
                          <label
                            className={`p-1 text-gray-700 text-[15px] font-bold`}
                          >
                            Captcha
                          </label>
                          <svg className="animate-spin h-8 w-8 text-gray-500" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <label
                            className={`mt-2 text-gray-700 text-[15px] font-bold`}
                          >
                            Enter CAPTCHA
                          </label>
                          <div className="flex flex-row items-center gap-2">
                            <img
                              src={loginCaptcha.captchaImage}
                              alt="CAPTCHA"
                              className="w-30 h-10 object-contain bg-gray-100 rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={fetchLoginCaptcha}
                              className="mt-2 text-sm text-[#ec7026] hover:text-[#d85d1a] font-['Semantic/Strong'] flex items-center"
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            <div className="w-3/5">
                              <LoginTextInput
                                labelClassName="text-[15px] font-bold"
                                name="captchaInput"
                                placeholder="Enter your captcha"
                                autoComplete="off"
                                type="text"
                                icon={
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {activeTab === 'active' && <button
                        type="button"
                        onClick={redirectToregister}
                        className="mt-2 text-sm text-right self-right text-[#ec7026] hover:text-[#d85d1a] font-['Semantic/Strong'] flex items-center underline"
                      >
                        Registration for Students
                      </button>}
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting || isLoading || captchaLoading}
                      className="w-max-content self-center p-8 uppercase bg-gradient-to-r from-[rgba(236,112,38,1)] to-[rgba(207,96,121,1)] text-white font-['Semantic/Strong'] font-semibold py-3 rounded-2xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      {(isLoading || isSubmitting
                        ? (
                          <div className="flex flex-row text-[15px] gap-2">
                            <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Signing in...
                          </div>
                        )
                        : <div className="flex flex-row text-[15px] gap-2">
                          <LogIn size={20} />
                          LOGIN
                        </div>
                      )}
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
            <Modal
              open={showForgotUsernameForm}
              onClose={() => setShowForgotUsernameForm(false)}
            >
              <div className="bg-white rounded-2xl p-2 w-[420px] animate-fadeIn">

                {/* Title */}
                <h3 className="font-semibold text-[20px] text-[#ec7026] mb-1">
                  Recover Username
                </h3>
                <p className="text-[12px] text-gray-500 mb-4">
                  Enter your registered email or mobile number
                </p>

                {/* Email / Mobile */}
                <input
                  type="text"
                  placeholder="Email or mobile number"
                  value={forgotData.identifier}
                  onChange={(e) => handleForgotChange("identifier", e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl 
  focus:ring-2 focus:ring-[#ec7026] focus:border-transparent transition-all mb-4"
                />


                {/* CAPTCHA Row */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <img
                      src={forgotCaptcha.captchaImage}
                      alt="CAPTCHA"
                      className="h-10 px-2 bg-gray-100 rounded-lg border border-gray-200"
                    />

                    <button
                      type="button"
                      onClick={fetchForgotCaptcha}
                      className="text-[#ec7026] hover:text-[#d85d1a] transition-colors"
                      title="Refresh CAPTCHA"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                  </div>

                  <input
                    type="text"
                    placeholder="Enter captcha"
                    autoComplete="off"
                    value={forgotData.captchaInput}
                    onChange={(e) => handleForgotChange("captchaInput", e.target.value)}
                    className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-xl 
  focus:ring-2 focus:ring-[#ec7026] focus:border-transparent transition-all"
                  />

                </div>

                {/* CTA */}
                <button
                  className="w-full py-2.5 bg-[#ec7026] text-white rounded-xl 
                 font-semibold hover:bg-[#d85d1a] transition-all duration-200"
                  onClick={sendOTP}
                >
                  Send Username
                </button>

              </div>
            </Modal>



            {/* Forgot Password Popup */}
            <Modal
              open={showForgotPasswordForm}
              onClose={() => {
                setShowForgotPasswordForm(false);
                setForgotPasswordStep("FORM");
                setForgotPasswordOTP("");

                setEmailError("");
                setMobileError("");

                // optional (recommended)
                setForgotPasswordData({
                  email: "",
                  mobile: "",
                  captchaInput: "",
                });
              }}
            >
              <div className="bg-white rounded-2xl p-4 w-[420px] animate-fadeIn">

                {forgotPasswordStep === "FORM" && (
                  <>
                    <h3 className="font-semibold text-[18px] text-[#ec7026] mb-1">
                      Reset Password
                    </h3>
                    <p className="text-[12px] text-gray-500 mb-4">
                      Enter your registered email & mobile number
                    </p>

                    <input
                      type="email"
                      placeholder="Registered email"
                      value={forgotPasswordData.email}
                      onChange={(e) =>
                        handleForgotPasswordChange("email", e.target.value)
                      }
                      className="w-full px-4 py-2.5 text-sm border rounded-xl mb-1"
                    />

                    {emailError && (
                      <p className={`text-xs mb-2 ${emailError.includes("exists") ? "text-green-600" : "text-red-500"
                        }`}>
                        {emailError === 'Email already exists' ? 'This Email is registered' : 'This Email is not registered'}
                      </p>
                    )}

                    <input
                      type="text"
                      placeholder="Registered mobile number"
                      value={forgotPasswordData.mobile}
                      onChange={(e) =>
                        handleForgotPasswordChange("mobile", e.target.value)
                      }
                      className="w-full px-4 py-2.5 text-sm border rounded-xl mb-1"
                    />

                    {mobileError && (
                      <p className={`text-xs mb-2 ${mobileError.includes("exists") ? "text-green-600" : "text-red-500"
                        }`}>
                        {mobileError === 'Mobile number already exists' ? 'This Mobile number is registered' : 'This Mobile number is not registered'}
                      </p>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={forgotCaptcha.captchaImage}
                        alt="CAPTCHA"
                        className="h-10 px-2 bg-gray-100 rounded-lg border"
                      />
                      <button onClick={fetchForgotCaptcha} className="text-[#ec7026]">
                        🔄
                      </button>
                      <input
                        type="text"
                        placeholder="Enter captcha"
                        value={forgotPasswordData.captchaInput}
                        onChange={(e) =>
                          handleForgotPasswordChange("captchaInput", e.target.value)
                        }
                        className="flex-1 px-4 py-2.5 text-sm border rounded-xl"
                      />
                    </div>

                    <button
                      onClick={sendForgotPasswordOTP}
                      className="w-full py-2.5 bg-[#ec7026] text-white rounded-xl font-semibold"
                    >
                      Send OTP
                    </button>
                  </>
                )}

                {forgotPasswordStep === "OTP" && (
                  <>
                    {/* OTP STEP */}
                    <h3 className="font-semibold text-[18px] text-[#ec7026] mb-1">
                      Verify OTP
                    </h3>
                    <p className="text-[12px] text-gray-500 mb-4">
                      Enter the 6-digit OTP sent to your email & mobile
                    </p>

                    <input
                      type="text"
                      maxLength={6}
                      value={forgotPasswordOTP}
                      onChange={(e) => setForgotPasswordOTP(e.target.value.replace(/\D/g, ""))}
                      placeholder="Enter 6 digit OTP"
                      className="w-full text-center tracking-widest text-lg px-4 py-3 border rounded-xl mb-4"
                    />

                    <button
                      onClick={verifyForgotPasswordOTP}
                      disabled={verifyingOTP}
                      className="w-full py-2.5 bg-[#ec7026] text-white rounded-xl font-semibold disabled:opacity-50"
                    >
                      {verifyingOTP ? "Verifying..." : "Verify OTP"}
                    </button>
                  </>
                )}



                {forgotPasswordStep === "RESET" && (
                  <>
                    <h3 className="font-semibold text-[18px] text-[#ec7026] mb-1">
                      Set New Password
                    </h3>
                    <p className="text-[12px] text-gray-500 mb-4">
                      Enter and confirm your new password
                    </p>

                    {/* NEW PASSWORD */}
                    <div className="relative mb-3">

                      {/* NEW PASSWORD */}
                      <div className="relative mb-3">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          placeholder="New password"
                          value={resetPasswordData.password}
                          onChange={(e) =>
                            handleResetPasswordChange("password", e.target.value)
                          }
                          onFocus={() => setIsNewPasswordFocused(true)}
                          onBlur={() => setIsNewPasswordFocused(false)}
                          className="w-full px-4 py-2.5 text-sm border rounded-xl pr-20"
                        />

                        {/* Eye Toggle */}
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((p) => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                          👁
                        </button>

                        {/* PASSWORD RULE TOOLTIP (TextInput-style) */}
                        {isNewPasswordFocused && resetPasswordData.password.length > 0 && (
                          <div
                            className="absolute z-50 top-full left-0 mt-1 w-[320px]
                 p-2 rounded-md bg-white border shadow-lg text-xs space-y-1"
                          >
                            <PasswordTooltip password={resetPasswordData.password} />
                          </div>
                        )}
                      </div>

                    </div>

                    {/* CONFIRM PASSWORD */}
                    <div className="relative mb-4">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={resetPasswordData.confirmPassword}
                        onChange={(e) =>
                          handleResetPasswordChange("confirmPassword", e.target.value)
                        }
                        className="w-full px-4 py-2.5 text-sm border rounded-xl pr-16"
                      />

                      {/* Eye Toggle */}
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(p => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                      >
                        👁
                      </button>
                    </div>

                    <button
                      onClick={resetPassword}
                      className="w-full py-2.5 bg-[#ec7026] text-white rounded-xl font-semibold"
                    >
                      Reset Password
                    </button>
                  </>
                )}


              </div>
            </Modal>




          </div>

          {/* Right Section: Professional Content */}
          <div className=" flex w-1/2 p-4 flex-col relative gap-7 text-justify">
            <h1
              // style={{ fontFamily }}
              className={`${headingSize} ${fontFamily} font-bold bg-gradient-to-r from-[rgba(236,112,38,1)] to-[rgba(207,96,121,1)] text-transparent bg-clip-text leading-tight tracking-wide`}
            >
              {selectedLanguage === 'english'
                ? content[0].name.english
                : content[0].name.hindi}
            </h1>

            <div className="flex flex-col gap-4">
              <p
                // style={{ fontFamily }}
                className={`${paragraphSize} ${fontFamily} text-gray-700 leading-relaxed`}>
                {selectedLanguage === 'english' ? content[1].description.english[0] : content[1].description.hindi[0]}
              </p>
            </div>
            <div className="">
              <h3
                // style={{ fontFamily }}
                className={`${subheadingSize} ${fontFamily} font-semibold text-gray-900 mb-4`}>
                {selectedLanguage === 'english' ? content[2].subheading.english : content[2].subheading.hindi}
              </h3>
              <ol className={`list-decimal list-inside space-y-3 text-[17px] ${paragraphSize} ${fontFamily}`}>
                <li className="text-gray-700">
                  {selectedLanguage === 'english'
                    ? content[3].points[0].english
                    : content[3].points[0].hindi}
                </li>
                <li className="text-gray-700">
                  {selectedLanguage === 'english'
                    ? content[3].points[1].english
                    : content[3].points[1].hindi}
                </li>
                <li className="text-gray-700">
                  {selectedLanguage === 'english'
                    ? content[3].points[2].english
                    : content[3].points[2].hindi}
                </li>
              </ol>

            </div>
            <button
              type="submit"
              className="text-[20px] text-[rgba(236,112,38,1)] font-['Semantic/Strong'] font-semibold flex flex-row items-center hover:underline"
              onClick={onClickAbout}
            >
              <ArrowRight />
              {selectedLanguage === 'english' ? content[4].link.english : content[4].link.hindi}
            </button>


          </div>
        </div>
      </div>

      {/* Footer */}
      {/* <footer className="w-full bg-white/80 backdrop-blur-sm border-t border-gray-200 py-4 text-center">
        <p className="text-sm text-gray-600 font-medium">
          © 2026 Rajbhasha Vibhag & C-DAC. All Rights Reserved
        </p>
      </footer> */}

    </>
  );

};

export default Login;